import { useRef, useState } from "react";
import { AppWindow, BookOpen, BookOpenCheck, Braces, FileCog, FileText, Package, Paperclip, X } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { SendDialog } from "@/components/studio/SendDialog";
import { PdfPreview } from "@/components/studio/PdfPreview";
import { PageTarget } from "@/components/studio/PageTarget";
import { api, downloadBlob, errMsg, fmtDate } from "@/lib/api";

const TILES = [
  { kind: "pdf", icon: FileText, title: "PDF guide", sub: "Typeset A4 guide", ext: ".pdf", need: "pdf_path" },
  { kind: "epub", icon: BookOpen, title: "Ebook (EPUB)", sub: "Apple Books, Google Play Books, Kindle", ext: ".epub", need: "epub_path" },
  { kind: "toolkit", icon: AppWindow, title: "Offline toolkit", sub: "Interactive single-file HTML app", ext: "-toolkit.html", need: "toolkit_path" },
  { kind: "package", icon: Package, title: "Store package", sub: "ZIP: PDF + EPUB + toolkit + sample + listing", ext: "-store-package.zip", need: "pdf_path" },
  { kind: "listing", icon: Braces, title: "Listing JSON", sub: "Product copy for the site", ext: "-listing.json" },
  { kind: "sample", icon: BookOpenCheck, title: "Free sample", sub: "Cover + Chapter I, watermarked", ext: "-free-sample.pdf", need: "sample_path" },
];

export const DownloadTiles = ({ p, theme, ensureSaved, onPatch, onStarted }) => {
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  const files = p.files || {};
  const slug = files.slug || "document";

  const build = async () => {
    setBusy(true);
    try {
      await ensureSaved();
      let r;
      try {
        r = await api.post(`/products/${p.id}/generate`);
      } catch (e) {
        if (e.response?.status !== 409 || !window.confirm(`${errMsg(e)} in the Fact check tab. Build the files anyway?`)) throw e;
        r = await api.post(`/products/${p.id}/generate?force=true`);
      }
      onPatch({ files: r.data });
      toast.success("PDF, ebook, toolkit and sample are ready");
    } catch (e) {
      if (e.response?.status !== 409) toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  const upload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const fd = new FormData();
    fd.append("file", f);
    try {
      const r = await api.post(`/products/${p.id}/attachment`, fd);
      onPatch({ attachment: r.data });
      toast.success("File attached");
    } catch (err) {
      toast.error(errMsg(err));
    }
    e.target.value = "";
  };

  return (
    <div data-testid="download-tiles">
      <div className="flex flex-wrap items-end justify-between gap-6 mb-5 pb-5 border-b border-[#2A2325]/10">
        <PdfPreview p={p} theme={theme} />
        <PageTarget p={p} theme={theme} ensureSaved={ensureSaved} onStarted={onStarted} onPatch={onPatch} />
      </div>
      <div className="flex flex-wrap items-center gap-3 mb-3">
        <Button onClick={build} disabled={busy} variant="outline" className="rounded-none gap-2 bg-transparent" style={{ borderColor: theme.dark, color: theme.dark }} data-testid="generate-files-btn">
          <FileCog size={15} /> {busy ? "Typesetting…" : files.pdf_path ? "Rebuild all files" : "Generate all files"}
        </Button>
        <div className="w-56"><SendDialog product={p} disabled={!files.pdf_path && !p.attachment} /></div>
        <input ref={fileRef} type="file" className="hidden" onChange={upload} data-testid="attachment-file-input" />
        {p.attachment ? (
          <span className="text-sm flex items-center gap-2 border border-[#2A2325]/12 px-3 py-1.5" data-testid="attachment-row">
            <Paperclip size={13} /> {p.attachment.filename}
            <button onClick={async () => { await api.delete(`/products/${p.id}/attachment`); onPatch({ attachment: null }); }} className="text-[#5C5255] hover:text-[#9B2C2C]" data-testid="attachment-remove-btn" aria-label="Remove file"><X size={13} /></button>
          </span>
        ) : (
          <button onClick={() => fileRef.current.click()} className="text-sm flex items-center gap-1.5 hover:underline" style={{ color: theme.dark }} data-testid="attachment-upload-btn"><Paperclip size={14} /> Attach extra file</button>
        )}
        {files.generated_at && <span className="text-xs text-[#5C5255] ml-auto" data-testid="files-generated-at">Built {fmtDate(files.generated_at)}, {new Date(files.generated_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })} · edition {files.edition || 1}</span>}
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 border border-[#2A2325]/10 bg-[#FFFDF9]">
        {TILES.map(({ kind, icon: Icon, title, sub, ext, need }, i) => {
          const ok = !need || files[need];
          const first = i === 0;
          return (
            <button
              key={kind}
              disabled={!ok}
              onClick={() => downloadBlob(`/products/${p.id}/files/${kind}`, `${slug}${ext}`)}
              className={`text-left p-6 border-r border-b md:border-b-0 border-[#2A2325]/10 transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${first ? "" : "hover:bg-[#F7F3EC]"}`}
              style={first ? { background: theme.dark, color: "#F3EBD6" } : {}}
              data-testid={`download-${kind}-btn`}
            >
              <Icon size={20} strokeWidth={1.4} style={{ color: theme.accent }} />
              <div className="font-playfair text-xl mt-5">{title}</div>
              <div className={`text-xs mt-1 ${first ? "text-[#F3EBD6]/70" : "text-[#5C5255]"}`}>{sub}</div>
              <div className="font-mono-meta text-[10px] tracking-[0.24em] mt-6" style={{ color: theme.accent }}>{ok ? "DOWNLOAD →" : "BUILD FIRST"}</div>
            </button>
          );
        })}
      </div>
    </div>
  );
};
